import {Button} from 'antd'
import {timetable_getMilestonesByCourseClass_Api} from 'apis'
import {getId} from 'apis/model/base'
import EmptyHolder from 'components/EmptyHolder'
import classNames from 'classnames'
import dayjs from 'dayjs'
import {formatDate} from 'helpers/datetimeHelpers'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useState} from 'react'
import {BsChevronDown} from 'react-icons/bs'
import {TimetableActionTypes} from 'views/AppLocals/Course'
import EntityList from 'views/AppLocals/EntityList'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {useTimetableCourseClassForm} from 'views/AppLocals/Timetable/useTimetableCourseClassForm'
import getTitle from 'helpers/getTitle'
import {GoDotFill} from "react-icons/go";
import {formatSimplifyDate} from "../../../../../helpers/dateUtils";
import {LayoutContext} from "../../../../../components/layouts/Default/LayoutContext";

export const CourseClassTimetablePanel = ({item}) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const course_class_id = getId(item)
    const can_edit = !!item?.edit
    const subjects = (item?.class_subjects ?? []).map((cs) => cs.subject).filter(Boolean)
    const [selectedSubjectId, setSelectedSubjectId] = useState(null)
    const [timetableRefreshKey, setTimetableRefreshKey] = useState(0)

    const [closedItems, setClosedItems] = useState({})

    const {modal, handleAction, renderContextMenu} = useTimetableCourseClassForm({
        course_class: item,
        refreshKey: timetableRefreshKey,
        setRefreshKey: setTimetableRefreshKey,
    })

    const entityKey = [course_class_id, selectedSubjectId, timetableRefreshKey].join('-')


    const renderItem = useCallback((timetable, index) => {
        if (timetable?.is_milestone) return null

        const {
            from_date, to_date, from_time, to_time,
            subject, teacher, notes, is_holiday,
            timetable_details = [], location_address,
        } = timetable ?? {}

        const dateRange = (from_date && to_date && from_date !== to_date)
            ? `${formatDate(from_date, 'DD/MM')} → ${formatDate(to_date, 'DD/MM/YYYY')}`
            : formatDate(from_date || to_date, 'DD/MM/YYYY')

        const completedCount = timetable_details.filter(detail =>
            dayjs(detail.start_time).isBefore(dayjs())
        ).length

        const totalCount = timetable_details.length

        const isCompleted = totalCount > 0 && completedCount === totalCount

        const isOpen =
            closedItems[getId(timetable)] === undefined
                ? !(isCompleted || is_holiday === 1)
                : !closedItems[getId(timetable)]

        return (
            <div
                key={index}
                className={classNames(
                    "flex flex-col border border-color-50 rounded-xl overflow-hidden transition-all duration-300",
                    isCompleted ? 'opacity-80 hover:opacity-100' : 'hover:shadow-md'
                )}>
                <div
                    className={classNames(
                        "px-3 md:px-4 py-3 flex items-start justify-between gap-2 custom-item-render relative",
                        isOpen ? 'border-b border-color-100 background-100' : 'hover:background-100'
                    )}>
                    <div className="mr-0 md:mr-2 w-full flex flex-col md:flex-row md:items-center gap-x-3 gap-y-1">
                        <div className="flex flex-col flex-1">
                            {subject && (
                                <span
                                    className={classNames(
                                        'font-bold truncate tracking-wide',
                                        isCompleted ? 'text-sm text-gray-700' : 'text-base text-gray-800'
                                    )}>
                                    {getTitle(subject)}
                                </span>
                            )}
                            <div className="flex flex-col md:flex-row gap-x-3">
                                {teacher && (
                                    <div className="space-x-1 text-xs tracking-wider">
                                        <span className="text-color-400">
                                            {`${t('teachers')}:`}
                                        </span>
                                        <span className="font-semibold text-gray-600">
                                            {getTitle(teacher)}
                                        </span>
                                    </div>
                                )}
                                {location_address && (
                                    <span className="font-medium text-xs text-gray-600 tracking-wider">
                                        <GoDotFill size={10} className="mr-1 text-color-500"/>
                                        {location_address}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 md:gap-4">
                            {isCompleted && (
                                <span
                                    style={{ padding: '0.125rem 0.75rem', borderRadius: 999 }}
                                    className="w-fit border border-green-300 bg-green-50 font-medium text-xs text-green-600 tracking-wider">
                                {t('finish')} {completedCount}/{totalCount}
                            </span>
                            )}
                            {is_holiday === 1 && (
                                <span
                                    style={{ padding: '0.125rem 0.75rem', borderRadius: 999 }}
                                    className="w-fit border border-primary-300 bg-primary-50 font-medium text-xs text-primary-600 tracking-wider">
                                    {t('holiday')}
                                </span>
                            )}
                            {!isLg && <div className="flex-1"/>}
                            {/*  Time arrange  */}
                            <div className="flex flex-col items-end tracking-wider">
                            <span className="text-2xs text-color-400">
                                {t('time takes place')}
                            </span>
                                <span className="font-semibold text-xs text-gray-700">
                                {dateRange}
                            </span>
                            </div>
                            {/* Action Open */}
                            <div
                                onClick={() => {
                                    const id = getId(timetable)
                                    setClosedItems(prev => ({
                                        ...prev,
                                        [id]: !prev[id]
                                    }))
                                }}
                                style={{ width: 28, height: 28, padding: '0.25rem', borderRadius: '0.5rem', cursor: 'pointer'}}
                                className="flex flex-center bg-secondary-50 hover:bg-secondary-500 text-secondary-600 hover:text-white">
                                <BsChevronDown
                                    size={14}
                                    style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.3s' }}
                                />
                            </div>
                        </div>
                    </div>
                    {can_edit && (
                        <div className="absolute top-3 right-3 contextIcon">
                            {renderContextMenu(timetable)}
                        </div>
                    )}
                </div>
                {isOpen && (
                    timetable_details.length > 0 ? (
                        <>
                            {notes && (
                                <span className="m-3 p-2 bg-secondary-50 rounded-lg text-xs text-secondary-400 italic leading-tight tracking-wider">
                                    <span className="font-medium text-secondary-600">
                                        {`* ${t('note')}: `}
                                    </span>
                                    {notes}
                                </span>
                            )}
                            <div className="flex flex-col divide-y divide-color-50">
                                {timetable_details.map((detail, di) => {
                                    const isPast = dayjs(detail.start_time).isBefore(dayjs())
                                    const dateStr = formatSimplifyDate(detail.start_time, '', 'dddd, DD MMMM, YYYY')
                                    const timeDetail = `${formatDate(detail.start_time, 'HH:mm')} – ${formatDate(detail.end_time, 'HH:mm')}`
                                    return (
                                        <div
                                            key={di}
                                            className="flex items-center gap-3 px-3 py-2">
                                            <div
                                                style={{ width: 28, height: 28, fontSize: 14}}
                                                className={classNames(
                                                'rounded-full flex flex-center flex-shrink-0 font-bold leading-none',
                                                isPast ? 'bg-green-100 text-green-600' : 'bg-blue-50 text-blue-600'
                                            )}>
                                                {di + 1}
                                            </div>
                                            <div
                                                className={classNames(
                                                    "flex flex-col flex-1 min-w-0",
                                                    isPast && 'line-through opacity-80'
                                                )}>
                                                <div className="font-semibold text-gray-700 tracking-wide truncate capitalize">
                                                    {dateStr}
                                                </div>
                                                <span className="font-medium text-xs text-color-300 tracking-wider">
                                                    {timeDetail}
                                                </span>

                                                {detail?.notes && (
                                                    <span className="text-xs text-color-400 italic leading-tight">
                                                        <span className="font-medium text-color-200">
                                                            {`* ${t('note')}: `}
                                                        </span>
                                                        {detail?.notes}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex flex-col items-end flex-shrink-0 gap-1">
                                                    <span className={classNames(
                                                        'text-xs font-medium px-2 py-0.5 rounded-full whitespace-no-wrap',
                                                        isPast ? 'bg-green-50 text-green-500' : 'bg-blue-50 text-blue-500'
                                                    )}>
                                                    {isPast ? t('done') : t('upcoming')}
                                                </span>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </>
                    ) : null
                )}
            </div>
        )
    }, [t, can_edit, renderContextMenu, closedItems])

    return (
        <div className="flex flex-col gap-3">
            {subjects.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={() => setSelectedSubjectId(null)}
                        className={classNames(
                            'px-3 py-1 text-xs font-medium rounded-full border transition-colors',
                            selectedSubjectId === null
                                ? 'bg-indigo-600 border-indigo-600 text-white'
                                : 'bg-transparent border-color-200 text-color-300 hover:border-indigo-400 hover:text-indigo-500'
                        )}>
                        {t('all')}
                    </button>
                    {subjects.map(s => (
                        <button
                            key={getId(s)}
                            onClick={() => setSelectedSubjectId(getId(s))}
                            className={classNames(
                                'px-3 py-1 text-xs font-medium rounded-full border transition-colors',
                                selectedSubjectId === getId(s)
                                    ? 'bg-indigo-600 border-indigo-600 text-white'
                                    : 'bg-transparent border-color-200 text-color-300 hover:border-indigo-400 hover:text-indigo-500'
                            )}>
                            {s?.title}
                        </button>
                    ))}
                </div>
            )}
            {can_edit && (
                <div className="flex justify-end">
                    <Button
                        color="primary" variant="filled"
                        className="rounded-md no-border font-medium text-xs"
                        onClick={() => handleAction(TimetableActionTypes.TIMETABLE_CREATE)}>
                        {t('add session')}
                    </Button>
                </div>
            )}
            <EntityList
                key={entityKey}
                refreshKey={entityKey}
                RenderEntity={React.Fragment}
                renderEmpty={() => <EmptyHolder title={null}/>}
                renderItem={renderItem}
                values={selectedSubjectId ? {subject_id: selectedSubjectId} : {}}
                apiInfo={timetable_getMilestonesByCourseClass_Api}
                query={bindQueryParam({id: course_class_id})}
                renderWrap={(child) => (
                    <div className="flex flex-col gap-4">
                        {child}
                    </div>
                )}
            />
            {modal}
        </div>
    )
}
